import { auth, authPersistenceReady } from '../firebase';
import { trigger } from './events';
import { isAuthDebugEnabled } from './authDebug';

class TokenManager {
  constructor() {
    this.tokenRefreshQueue = [];
    this.isRefreshing = false;
    this.refreshTimer = null;
    this.authState = 'unknown'; // 'firebase', 'internal', 'none'
    this.lastRefreshTime = 0;
    this.refreshCooldown = 30000; // 30 seconds cooldown between refreshes
    this.debugMode = isAuthDebugEnabled();

    if (this.debugMode) {
      console.log('🔧 TokenManager initialized');
    }
  }

  // Debug logging
  debug(message, data = null) {
    if (this.debugMode) {
      console.log(`🔧 TokenManager: ${message}`, data);
    }
  }

  // Proactive token refresh before expiry
  scheduleTokenRefresh(token, expiryTime) {
    this.debug('Scheduling token refresh', { expiryTime });

    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const refreshTime = expiryTime - 5 * 60 * 1000; // 5 minutes before expiry
    const delay = refreshTime - Date.now();

    if (delay > 0) {
      this.debug(`Token refresh scheduled in ${Math.round(delay / 1000)}s`);
      this.refreshTimer = setTimeout(() => {
        this.debug('Scheduled token refresh triggered');
        this.refreshToken();
      }, delay);
    } else {
      this.debug('Token already expired, refreshing immediately');
      this.refreshToken();
    }
  }

  // Queue-based refresh to prevent multiple simultaneous refreshes
  async refreshToken({ force = false } = {}) {
    this.debug('refreshToken called', {
      isRefreshing: this.isRefreshing,
      queueLength: this.tokenRefreshQueue.length,
      force,
    });

    if (this.isRefreshing) {
      this.debug('Token refresh already in progress, queuing request');
      return new Promise((resolve) => {
        this.tokenRefreshQueue.push(resolve);
      });
    }

    // Check cooldown
    const now = Date.now();
    if (!force && now - this.lastRefreshTime < this.refreshCooldown) {
      this.debug('Token refresh in cooldown period, skipping');
      return localStorage.getItem('accessToken');
    }

    this.isRefreshing = true;
    this.lastRefreshTime = now;

    try {
      this.debug('Starting token refresh');
      const newToken = await this.performTokenRefresh({ force });
      this.debug('Token refresh successful', { tokenLength: newToken?.length });

      this.notifyTokenRefresh(newToken);
      this.processQueue(newToken);

      return newToken;
    } catch (error) {
      this.debug('Token refresh failed', error);
      this.handleRefreshError(error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  async performTokenRefresh({ force = true } = {}) {
    this.debug('performTokenRefresh called');

    // Try Firebase auth first
    await authPersistenceReady;
    const user = auth.currentUser;
    if (user) {
      this.debug('Using Firebase auth for token refresh');
      this.authState = 'firebase';

      try {
        const token = await user.getIdToken(force);
        const tokenResult = await user.getIdTokenResult();

        // Schedule next refresh
        this.scheduleTokenRefresh(
          token,
          new Date(tokenResult.expirationTime).getTime()
        );

        // Store token
        localStorage.setItem('accessToken', token);
        localStorage.setItem('tokenExpiry', tokenResult.expirationTime);

        return token;
      } catch (error) {
        this.debug('Firebase token refresh failed', error);
        throw error;
      }
    }

    // Fallback to internal token refresh
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      this.debug('Using internal token refresh');
      this.authState = 'internal';

      try {
        const response = await fetch('/api/tokens', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: localStorage.getItem('accessToken'),
            refresh_token: refreshToken,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('accessToken', data.access_token);
          localStorage.setItem('refreshToken', data.refresh_token);

          // Schedule refresh for internal tokens (assuming 1 hour expiry)
          const expiryTime = Date.now() + 60 * 60 * 1000;
          this.scheduleTokenRefresh(data.access_token, expiryTime);

          return data.access_token;
        } else {
          throw new Error(`Token refresh failed: ${response.status}`);
        }
      } catch (error) {
        this.debug('Internal token refresh failed', error);
        throw error;
      }
    }

    throw new Error('No authentication method available for token refresh');
  }

  notifyTokenRefresh(newToken) {
    this.debug('Notifying token refresh');
    trigger('tokenRefreshed', { token: newToken, timestamp: Date.now() });
  }

  processQueue(newToken) {
    this.debug('Processing refresh queue', {
      queueLength: this.tokenRefreshQueue.length,
    });

    while (this.tokenRefreshQueue.length > 0) {
      const resolve = this.tokenRefreshQueue.shift();
      resolve(newToken);
    }
  }

  handleRefreshError(error) {
    this.debug('Handling refresh error', error);

    // Clear stored tokens on auth failure
    if (
      error.code === 'auth/user-token-expired' ||
      error.code === 'auth/user-not-found'
    ) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiry');
      trigger('authError', {
        error: 'Authentication expired',
        code: error.code,
      });
    }

    // Process queue with error
    while (this.tokenRefreshQueue.length > 0) {
      const resolve = this.tokenRefreshQueue.shift();
      resolve(null);
    }
  }

  // Get current valid token
  async getValidToken() {
    this.debug('getValidToken called');

    // Check if we have a stored token
    const storedToken = localStorage.getItem('accessToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (storedToken) {
      // If we have a stored token with expiry info, check if it's still valid
      if (tokenExpiry) {
        const expiryTime = new Date(tokenExpiry).getTime();
        const now = Date.now();

        // If token expires in more than 10 minutes, return it
        if (expiryTime - now > 10 * 60 * 1000) {
          this.debug('Using stored valid token');
          return storedToken;
        }

        // If token expires soon, refresh it
        this.debug('Token expires soon, refreshing');
        return await this.refreshToken();
      } else {
        // For internal tokens without expiry info, assume they're valid
        // (they will be refreshed by the backend when they expire)
        this.debug('Using stored internal token without expiry info');
        return storedToken;
      }
    }

    // No stored token, try to get from Firebase
    await authPersistenceReady;
    const user = auth.currentUser;
    if (user) {
      this.debug('Getting fresh Firebase token');
      const token = await user.getIdToken();
      const tokenResult = await user.getIdTokenResult();

      localStorage.setItem('accessToken', token);
      localStorage.setItem('tokenExpiry', tokenResult.expirationTime);

      this.scheduleTokenRefresh(
        token,
        new Date(tokenResult.expirationTime).getTime()
      );

      return token;
    }

    throw new Error('No valid authentication token available');
  }

  // Cleanup method
  cleanup() {
    this.debug('Cleaning up TokenManager');
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.tokenRefreshQueue = [];
    this.isRefreshing = false;
  }

  // Get current auth state
  getAuthState() {
    return this.authState;
  }

  // Check if currently refreshing
  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;
